import { type LucideIcon, PackageX, RotateCcw, ShoppingCart, Trash2 } from "lucide-react";
import type { ReactNode } from "react";

import { COLORS } from "@busright/ui";

import { formatInt } from "@/lib/format";

// Real replacement-demand signal from /api/lifecycle: replacement orders + returned/restocked/
// scrapped units (EOL via returns + RMA dispositions). All measured counts — no projection here.

export interface ReplacementSignal {
  readonly replacementOrders: number;
  readonly returnedUnits: number;
  readonly restockedUnits: number;
  readonly scrappedUnits: number;
}

interface FlowItem {
  readonly key: keyof ReplacementSignal;
  readonly label: string;
  readonly hint: string;
  readonly icon: LucideIcon;
}

const ITEMS: readonly FlowItem[] = [
  {
    key: "replacementOrders",
    label: "Replacement orders",
    hint: "Replacement order-type",
    icon: ShoppingCart,
  },
  { key: "returnedUnits", label: "Returned", hint: "Units returned (RMA)", icon: PackageX },
  { key: "restockedUnits", label: "Restocked", hint: "Returns put back in stock", icon: RotateCcw },
  { key: "scrappedUnits", label: "Scrapped", hint: "EOL — scrap disposition", icon: Trash2 },
];

export interface LcReplacementFlowProps {
  readonly signal: ReplacementSignal;
}

export const LcReplacementFlow = ({ signal }: LcReplacementFlowProps): ReactNode => (
  <div className="grid cols-2" style={{ gap: 12 }}>
    {ITEMS.map((item) => {
      const Icon = item.icon;
      return (
        <div
          key={item.key}
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            padding: "12px 14px",
            background: "var(--br-surface-2)",
            border: "1px solid var(--br-border)",
            borderRadius: "var(--br-radius-md)",
          }}
        >
          <Icon size={20} aria-hidden style={{ color: COLORS.textSecondary, flexShrink: 0 }} />
          <div>
            <div className="br-tabular-nums" style={{ fontSize: 22, fontWeight: 600 }}>
              {formatInt(signal[item.key])}
            </div>
            <div style={{ fontSize: 12, color: "var(--br-text-secondary)" }}>{item.label}</div>
            <div style={{ fontSize: 10, color: "var(--br-text-muted)" }}>{item.hint}</div>
          </div>
        </div>
      );
    })}
  </div>
);
